import { useState, useEffect, useMemo, memo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { X, Loader2, Search, SlidersHorizontal, ChevronDown, Trash2 } from 'lucide-react';
import ProductCard from '../components/products/ProductCard';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { ProductListJsonLd } from '../components/seo/JsonLd';
import api from '../lib/api';

const MASCOT_SAD = 'https://res.cloudinary.com/dcu6hlupu/image/upload/v1774552344/carvalho-deals/brand/mascot.png';

const stores = [
  { id: 'amazon', name: 'Amazon', color: '#FF9900' },
  { id: 'mercadolivre', name: 'Mercado Livre', color: '#FFE600' },
  { id: 'shopee', name: 'Shopee', color: '#EE4D2D' },
  { id: 'temu', name: 'Temu', color: '#FB7701' },
  { id: 'shein', name: 'Shein', color: '#000000' },
  { id: 'hotmart', name: 'Hotmart', color: '#F04E23' },
  { id: 'kabum', name: 'Kabum', color: '#FF6500' },
  { id: 'casasbahia', name: 'Casas Bahia', color: '#0066CC' },
  { id: 'americanas', name: 'Americanas', color: '#E60014' },
];

const sortOptions = [
  { id: 'recent', label: 'Mais Recentes' },
  { id: 'price_asc', label: 'Menor Preco' },
  { id: 'discount', label: 'Maior Desconto' },
];

// Componente de filtros extraído e memoizado para evitar re-renders
const FilterContent = memo(({ 
  inDrawer = false, 
  searchQuery, 
  setSearchQuery, 
  selectedStores, 
  toggleStore, 
  selectedCategories, 
  toggleCategory, 
  priceRange, 
  setPriceRange, 
  categories, 
  clearAllFilters, 
  setDrawerOpen, 
  activeFilterCount 
}) => (
  <div className="space-y-6">
    {/* Clear all — always visible at top */}
    <div className="flex items-center justify-between">
      <h2 className="font-heading text-lg font-bold text-slate-900">Filtros</h2>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          clearAllFilters();
          if (inDrawer) setDrawerOpen(false);
        }}
        className={`text-xs gap-1.5 ${activeFilterCount > 0 ? 'text-red-500 hover:text-red-600 hover:bg-red-50' : 'text-slate-400'}`}
        data-testid="clear-filters-btn"
      >
        <Trash2 className="w-3.5 h-3.5" />
        Limpar Filtros
      </Button>
    </div>

    {/* Search */}
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Buscar..."
        className="w-full h-10 pl-10 pr-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 transition-all"
        data-testid="filter-search-input"
      />
    </div>

    {/* Stores */}
    <div>
      <h3 className="font-semibold text-sm text-slate-900 mb-3 flex items-center justify-between">
        Lojas
        {selectedStores.length > 0 && (
          <span className="text-[11px] bg-orange-100 text-orange-700 font-medium px-2 py-0.5 rounded-full">
            {selectedStores.length}
          </span>
        )}
      </h3>
      <div className="space-y-1.5">
        {stores.map((store) => (
          <label
            key={store.id}
            className={`flex items-center gap-3 cursor-pointer p-2 rounded-lg transition-colors ${
              selectedStores.includes(store.id) ? 'bg-orange-50' : 'hover:bg-slate-50'
            }`}
            data-testid={`store-filter-${store.id}`}
          >
            <input
              type="checkbox"
              checked={selectedStores.includes(store.id)}
              onChange={() => toggleStore(store.id)}
              className="w-4 h-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
            />
            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: store.color }} />
            <span className="text-sm text-slate-700">{store.name}</span>
          </label>
        ))}
      </div>
    </div>

    {/* Price Range */}
    <div>
      <h3 className="font-semibold text-sm text-slate-900 mb-3">Faixa de Preco</h3>
      <div className="space-y-4">
        <Slider value={priceRange} onValueChange={setPriceRange} max={5000} step={50} className="w-full" />
        <div className="flex items-center justify-between text-sm">
          <span className="bg-slate-100 text-slate-700 font-medium px-2.5 py-1 rounded-md text-xs">
            R$ {priceRange[0]}
          </span>
          <span className="text-slate-400 text-xs">ate</span>
          <span className="bg-slate-100 text-slate-700 font-medium px-2.5 py-1 rounded-md text-xs">
            R$ {priceRange[1]}
          </span>
        </div>
      </div>
    </div>

    {/* Categories */}
    {categories.length > 0 && (
      <div>
        <h3 className="font-semibold text-sm text-slate-900 mb-3 flex items-center justify-between">
          Categorias
          {selectedCategories.length > 0 && (
            <span className="text-[11px] bg-orange-100 text-orange-700 font-medium px-2 py-0.5 rounded-full">
              {selectedCategories.length}
            </span>
          )}
        </h3>
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {categories.map((category) => (
            <label
              key={category.id}
              className={`flex items-center gap-3 cursor-pointer p-2 rounded-lg transition-colors ${
                selectedCategories.includes(category.id) ? 'bg-orange-50' : 'hover:bg-slate-50'
              }`}
              data-testid={`category-filter-${category.id}`}
            >
              <input
                type="checkbox"
                checked={selectedCategories.includes(category.id)}
                onChange={() => toggleCategory(category.id)}
                className="w-4 h-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-lg">{category.icon || '📦'}</span>
              <span className="text-sm text-slate-700">{category.name}</span>
            </label>
          ))}
        </div>
      </div>
    )}
  </div>
));

const Ofertas = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sortBy, setSortBy] = useState('recent');
  const [sortOpen, setSortOpen] = useState(false);

  const [selectedStores, setSelectedStores] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 5000]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || searchParams.get('search') || '');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productsRes, catsRes] = await Promise.all([
          api.get('/products', { params: { limit: 500 } }),
          api.get('/categories'),
        ]);
        setAllProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
        setCategories(Array.isArray(catsRes.data) ? catsRes.data : []);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const q = searchParams.get('q') || searchParams.get('search') || '';
    if (q) setSearchQuery(q);
  }, [searchParams]);

  // Palavras comuns em português que não devem ser buscadas sozinhas
  const commonWords = ['de', 'da', 'do', 'das', 'dos', 'em', 'na', 'no', 'nas', 'nos', 'para', 'por', 'com', 'sem', 'que', 'um', 'uma', 'os', 'as', 'ao', 'e'];

  const filteredProducts = useMemo(() => {
    let result = [...allProducts];

    if (selectedStores.length > 0) {
      result = result.filter((p) => selectedStores.includes(p.platform));
    }
    if (selectedCategories.length > 0) {
      result = result.filter((p) => selectedCategories.includes(p.category_id));
    }
    result = result.filter((p) => p.current_price >= priceRange[0] && p.current_price <= priceRange[1]);

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      
      // Se for palavra comum e curta, não filtra
      if (commonWords.includes(q)) {
        // Retorna todos os produtos sem filtro de busca
      } else {
        // Filtra para mostrar apenas produtos onde uma palavra começa com o termo
        result = result.filter((p) => {
          const name = p.name.toLowerCase();
          const words = name.split(/[\s\-,.()+]+/);
          return words.some(w => w.startsWith(q));
        });
        
        // Ordena por relevância: prioriza produtos cujo nome ou palavras começam com o termo
        result.sort((a, b) => {
          const nameA = a.name.toLowerCase();
          const nameB = b.name.toLowerCase();
          
          // Prioridade 1: Nome começa exatamente com o termo
          const aStartsWithTerm = nameA.startsWith(q);
          const bStartsWithTerm = nameB.startsWith(q);
          if (aStartsWithTerm && !bStartsWithTerm) return -1;
          if (!aStartsWithTerm && bStartsWithTerm) return 1;
          
          // Prioridade 2: Alguma palavra do nome começa com o termo
          const wordsA = nameA.split(/[\s\-,]+/);
          const wordsB = nameB.split(/[\s\-,]+/);
          const aWordStartsWith = wordsA.some(w => w.startsWith(q));
          const bWordStartsWith = wordsB.some(w => w.startsWith(q));
          if (aWordStartsWith && !bWordStartsWith) return -1;
          if (!aWordStartsWith && bWordStartsWith) return 1;
          
          return 0;
        });
      }
    }

    // Sort adicional (sobrescreve ordenação por relevância se usuário escolher)
    if (sortBy === 'price_asc') {
      result.sort((a, b) => a.current_price - b.current_price);
    } else if (sortBy === 'discount') {
      const disc = (p) =>
        p.old_price && p.old_price > p.current_price
          ? ((p.old_price - p.current_price) / p.old_price) * 100
          : 0;
      result.sort((a, b) => disc(b) - disc(a));
    }

    return result;
  }, [allProducts, selectedStores, selectedCategories, priceRange, searchQuery, sortBy]);

  const toggleStore = (storeId) => {
    setSelectedStores((prev) => (prev.includes(storeId) ? prev.filter((id) => id !== storeId) : [...prev, storeId]));
  };

  const toggleCategory = (categoryId) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
    );
  };

  const clearAllFilters = () => {
    setSelectedStores([]);
    setSelectedCategories([]);
    setPriceRange([0, 5000]);
    setSearchQuery('');
    setSortBy('recent');
    setSearchParams({});
  };

  const activeFilterCount =
    selectedStores.length + selectedCategories.length + (priceRange[0] !== 0 || priceRange[1] !== 5000 ? 1 : 0);

  // Close sort dropdown on click outside
  useEffect(() => {
    if (!sortOpen) return;
    const handler = () => setSortOpen(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [sortOpen]);

  // Props comuns para o FilterContent
  const filterProps = {
    searchQuery,
    setSearchQuery,
    selectedStores,
    toggleStore,
    selectedCategories,
    toggleCategory,
    priceRange,
    setPriceRange,
    categories,
    clearAllFilters,
    setDrawerOpen,
    activeFilterCount
  };

  return (
    <Layout>
      <ProductListJsonLd products={filteredProducts.slice(0, 20)} />
      <div className="min-h-screen bg-[#f8fafc] pt-4 md:pt-8" data-testid="ofertas-page">
        <div className="container mx-auto px-4 max-w-7xl py-6 md:py-8">
          {/* Page Header */}
          <div className="mb-6">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-1">Todas as Ofertas</h1>
            <p className="text-slate-500 text-sm">Encontre os melhores precos nas maiores lojas do Brasil</p>
          </div>

          {/* Active filter tags */}
          {activeFilterCount > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="active-filters">
              {selectedStores.map((id) => {
                const store = stores.find((s) => s.id === id);
                return store ? (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-full text-xs font-medium shadow-sm"
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: store.color }} />
                    {store.name}
                    <button onClick={() => toggleStore(id)} className="ml-0.5 text-slate-400 hover:text-slate-600">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ) : null;
              })}
              {selectedCategories.map((id) => {
                const cat = categories.find((c) => c.id === id);
                return cat ? (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-full text-xs font-medium shadow-sm"
                  >
                    {cat.icon || '📦'} {cat.name}
                    <button onClick={() => toggleCategory(id)} className="ml-0.5 text-slate-400 hover:text-slate-600">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ) : null;
              })}
              {searchQuery && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-full text-xs font-medium shadow-sm">
                  Busca: {searchQuery}
                  <button onClick={() => setSearchQuery('')} className="ml-0.5 text-slate-400 hover:text-slate-600">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              <button
                onClick={clearAllFilters}
                className="text-red-500 hover:text-red-600 text-xs font-medium underline underline-offset-2"
              >
                Limpar tudo
              </button>
            </div>
          )}

          <div className="flex gap-8">
            {/* Sidebar — Desktop sticky */}
            <aside className="hidden lg:block w-72 flex-shrink-0">
              <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm sticky top-[100px]" style={{ maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}>
                <FilterContent {...filterProps} />
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {/* Toolbar: counter + sort */}
              <div className="flex items-center justify-between mb-5">
                <p className="text-slate-600 text-sm font-medium" data-testid="ofertas-count">
                  {loading ? (
                    'Carregando...'
                  ) : (
                    <>
                      <span className="text-slate-900 font-bold">{filteredProducts.length}</span>{' '}
                      oferta{filteredProducts.length !== 1 ? 's' : ''} encontrada
                      {filteredProducts.length !== 1 ? 's' : ''}
                    </>
                  )}
                </p>

                {/* Sort Dropdown */}
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSortOpen(!sortOpen);
                    }}
                    className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 hover:border-slate-300 transition-colors shadow-sm"
                    data-testid="sort-dropdown-btn"
                  >
                    <span className="text-slate-500 text-xs hidden sm:inline">Ordenar:</span>
                    <span className="font-medium">{sortOptions.find((o) => o.id === sortBy)?.label}</span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${sortOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {sortOpen && (
                    <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-20 min-w-[180px] py-1 overflow-hidden">
                      {sortOptions.map((opt) => (
                        <button
                          key={opt.id}
                          onClick={() => {
                            setSortBy(opt.id);
                            setSortOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                            sortBy === opt.id
                              ? 'bg-orange-50 text-orange-700 font-semibold'
                              : 'text-slate-700 hover:bg-slate-50'
                          }`}
                          data-testid={`sort-option-${opt.id}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Products */}
              {loading ? (
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm">
                      <div className="skeleton aspect-square w-full" />
                      <div className="p-3 space-y-2">
                        <div className="skeleton h-4 w-full" />
                        <div className="skeleton h-5 w-1/2" />
                        <div className="skeleton h-9 w-full rounded-lg" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-16 md:py-24 bg-white rounded-2xl border border-slate-200/80 shadow-sm" data-testid="empty-state">
                  <div className="max-w-sm mx-auto px-4">
                    <img
                      src={MASCOT_SAD}
                      alt="Nenhum resultado"
                      className="w-24 h-24 mx-auto mb-5 opacity-60"
                      style={{ filter: 'grayscale(40%)' }}
                    />
                    <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                      Ops! Nenhuma oferta encontrada
                    </h3>
                    <p className="text-slate-500 text-sm mb-6 leading-relaxed">
                      {searchQuery
                        ? `Nao encontramos ofertas para "${searchQuery}". Tente outros termos!`
                        : 'Nao encontramos ofertas com esses filtros. Que tal tentar outras opcoes?'}
                    </p>
                    <Button
                      onClick={clearAllFilters}
                      className="bg-[#FF4500] hover:bg-[#E03D00] text-white font-semibold rounded-xl px-6 py-2.5"
                      data-testid="empty-state-clear-btn"
                    >
                      Ver todas as ofertas
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                  {filteredProducts.map((product, idx) => (
                    <div key={product.id} className="stagger-item" style={{ animationDelay: `${idx * 50}ms` }}>
                      <ProductCard product={product} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Floating Filter Button + Drawer */}
        <div className="lg:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <Button
                className="bg-slate-900 hover:bg-slate-800 text-white shadow-xl shadow-slate-900/30 rounded-full h-12 px-6 font-semibold text-sm"
                data-testid="mobile-filter-btn"
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Filtrar
                {activeFilterCount > 0 && (
                  <span className="ml-2 bg-orange-500 text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[85vh] rounded-t-3xl p-6 overflow-y-auto">
              <FilterContent {...filterProps} inDrawer />
              <div className="mt-6 sticky bottom-0 bg-white pt-4 border-t border-slate-100">
                <Button
                  onClick={() => setDrawerOpen(false)}
                  className="w-full bg-[#FF4500] hover:bg-[#E03D00] text-white font-semibold py-6 rounded-xl text-base"
                  data-testid="mobile-apply-filters-btn"
                >
                  Ver {filteredProducts.length} resultado{filteredProducts.length !== 1 ? 's' : ''}
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </Layout>
  );
};

export default Ofertas;
